import { supabase } from './supabase';

export interface CoachRespondentNote {
  id: string;
  coachId: string;
  respondentId: string;
  body: string;
  includeInAiBrief: boolean;
  updatedAt: string;
}

export async function fetchCoachRespondentNote(
  coachId: string,
  respondentId: string,
): Promise<CoachRespondentNote | null> {
  const { data } = await supabase
    .from('coach_respondent_notes')
    .select('*')
    .eq('coach_id', coachId)
    .eq('respondent_id', respondentId)
    .maybeSingle();

  if (!data) return null;
  return {
    id: data.id,
    coachId: data.coach_id,
    respondentId: data.respondent_id,
    body: data.body || '',
    includeInAiBrief: data.include_in_ai_brief,
    updatedAt: data.updated_at,
  };
}

export async function saveCoachRespondentNote(
  coachId: string,
  respondentId: string,
  body: string,
  includeInAiBrief: boolean,
): Promise<void> {
  const { data: existing } = await supabase
    .from('coach_respondent_notes')
    .select('id')
    .eq('coach_id', coachId)
    .eq('respondent_id', respondentId)
    .maybeSingle();

  const payload = {
    coach_id: coachId,
    respondent_id: respondentId,
    body,
    include_in_ai_brief: includeInAiBrief,
    updated_at: new Date().toISOString(),
  };

  if (existing) {
    const { error } = await supabase
      .from('coach_respondent_notes')
      .update(payload)
      .eq('id', existing.id);
    if (error) throw error;
  } else {
    const { error } = await supabase
      .from('coach_respondent_notes')
      .insert(payload);
    if (error) throw error;
  }
}

export interface AICoachConfig {
  id: string;
  coachId: string;
  hasOpenaiApiKey: boolean;
  openaiAssistantId: string;
  hasElevenlabsApiKey: boolean;
  elevenlabsAgentId: string;
  isEnabled: boolean;
  cloneName: string;
  cloneDescription: string;
  coachingStyle: string;
  idealClient: string;
  principles: string;
  dosAndDonts: string;
  disclaimers: string;
  welcomeMessage: string;
  starterQuestions: string[];
  clientAccessEnabled: boolean;
  vectorStoreId: string;
  setupCompleted: boolean;
}

export interface AICoachAccess {
  id: string;
  coachId: string;
  respondentId: string;
  chatEnabled: boolean;
  voiceEnabled: boolean;
  maxChatMessages: number;
  maxVoiceMinutes: number;
  maxTokensPerMonth: number;
}

export interface AICoachUsageLog {
  id: string;
  coachId: string;
  respondentId: string;
  type: 'chat' | 'voice';
  durationSeconds: number | null;
  messageCount: number;
  threadId: string | null;
  createdAt: string;
}

export interface UsageSummary {
  respondentId: string;
  respondentName: string;
  respondentEmail: string;
  totalChatMessages: number;
  totalVoiceMinutes: number;
  totalTokens: number;
  maxTokensPerMonth: number;
  maxVoiceMinutes: number;
  lastActivity: string | null;
}

export async function fetchCoachConfig(coachId: string): Promise<AICoachConfig | null> {
  const { data } = await supabase
    .from('ai_coach_configs')
    .select('id, coach_id, openai_assistant_id, elevenlabs_agent_id, is_enabled, openai_api_key_secret_id, elevenlabs_api_key_secret_id, clone_name, clone_description, coaching_style, ideal_client, principles, dos_and_donts, disclaimers, welcome_message, starter_questions, client_access_enabled, vector_store_id, setup_completed')
    .eq('coach_id', coachId)
    .maybeSingle();

  if (!data) return null;

  return {
    id: data.id,
    coachId: data.coach_id,
    hasOpenaiApiKey: !!data.openai_api_key_secret_id,
    openaiAssistantId: data.openai_assistant_id,
    hasElevenlabsApiKey: !!data.elevenlabs_api_key_secret_id,
    elevenlabsAgentId: data.elevenlabs_agent_id || '',
    isEnabled: data.is_enabled,
    cloneName: data.clone_name || '',
    cloneDescription: data.clone_description || '',
    coachingStyle: data.coaching_style || '',
    idealClient: data.ideal_client || '',
    principles: data.principles || '',
    dosAndDonts: data.dos_and_donts || '',
    disclaimers: data.disclaimers || '',
    welcomeMessage: data.welcome_message || 'Welcome! How can I help you today?',
    starterQuestions: data.starter_questions || [],
    clientAccessEnabled: data.client_access_enabled || false,
    vectorStoreId: data.vector_store_id || '',
    setupCompleted: data.setup_completed || false,
  };
}

export interface CloneSetupData {
  cloneName: string;
  cloneDescription: string;
  coachingStyle: string;
  idealClient: string;
  principles: string;
  dosAndDonts: string;
  disclaimers: string;
  welcomeMessage: string;
  starterQuestions: string[];
  clientAccessEnabled: boolean;
}

function compileSystemInstructions(data: CloneSetupData): string {
  const sections: string[] = [];

  if (data.cloneName) {
    sections.push(`You are ${data.cloneName}.`);
  }
  if (data.cloneDescription) {
    sections.push(`Your purpose: ${data.cloneDescription}`);
  }
  if (data.coachingStyle) {
    sections.push(`Communication style and tone: ${data.coachingStyle}`);
  }
  if (data.idealClient) {
    sections.push(`You are designed to help: ${data.idealClient}`);
  }
  if (data.principles) {
    sections.push(`Core coaching principles and methodology:\n${data.principles}`);
  }
  if (data.dosAndDonts) {
    sections.push(`Important guidelines (do's and don'ts):\n${data.dosAndDonts}`);
  }
  if (data.disclaimers) {
    sections.push(`Disclaimers and boundaries:\n${data.disclaimers}`);
  }

  sections.push(
    'Always be supportive, professional, and coaching-oriented. Never provide medical, psychological, or diagnostic advice. Focus on personal development, growth, and potential.'
  );

  return sections.join('\n\n');
}

export async function saveDraftSetup(coachId: string, data: CloneSetupData): Promise<void> {
  const payload = {
    coach_id: coachId,
    clone_name: data.cloneName,
    clone_description: data.cloneDescription,
    coaching_style: data.coachingStyle,
    ideal_client: data.idealClient,
    principles: data.principles,
    dos_and_donts: data.dosAndDonts,
    disclaimers: data.disclaimers,
    welcome_message: data.welcomeMessage,
    starter_questions: data.starterQuestions.filter(q => q.trim() !== ''),
    client_access_enabled: data.clientAccessEnabled,
    updated_at: new Date().toISOString(),
  };

  const { data: existing } = await supabase
    .from('ai_coach_configs')
    .select('id')
    .eq('coach_id', coachId)
    .maybeSingle();

  if (existing) {
    const { error } = await supabase
      .from('ai_coach_configs')
      .update(payload)
      .eq('id', existing.id);
    if (error) throw error;
  } else {
    const { error } = await supabase
      .from('ai_coach_configs')
      .insert({ ...payload, is_enabled: false, setup_completed: false });
    if (error) throw error;
  }
}

export async function saveCloneSetup(coachId: string, data: CloneSetupData): Promise<void> {
  const systemInstructions = compileSystemInstructions(data);

  const payload = {
    coach_id: coachId,
    clone_name: data.cloneName,
    clone_description: data.cloneDescription,
    coaching_style: data.coachingStyle,
    ideal_client: data.idealClient,
    principles: data.principles,
    dos_and_donts: data.dosAndDonts,
    disclaimers: data.disclaimers,
    welcome_message: data.welcomeMessage,
    starter_questions: data.starterQuestions,
    client_access_enabled: data.clientAccessEnabled,
    system_instructions_compiled: systemInstructions,
    setup_completed: true,
    updated_at: new Date().toISOString(),
  };

  const { data: existing } = await supabase
    .from('ai_coach_configs')
    .select('id')
    .eq('coach_id', coachId)
    .maybeSingle();

  if (existing) {
    const { error } = await supabase
      .from('ai_coach_configs')
      .update(payload)
      .eq('id', existing.id);
    if (error) throw error;
  } else {
    const { error } = await supabase
      .from('ai_coach_configs')
      .insert({ ...payload, is_enabled: false });
    if (error) throw error;
  }
}

export async function createVectorStore(coachId: string): Promise<string> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Not authenticated');

  const res = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/manage-vector-store?action=create-store`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
      },
    }
  );

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to create vector store');
  }

  const { vector_store_id } = await res.json();
  return vector_store_id;
}

export async function uploadKnowledgeFile(file: File): Promise<{ file_id: string; filename: string }> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Not authenticated');

  const formData = new FormData();
  formData.append('file', file);

  const res = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/manage-vector-store?action=upload-file`,
    {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
      },
      body: formData,
    }
  );

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to upload file');
  }

  return res.json();
}

export interface KnowledgeFile {
  id: string;
  openai_file_id: string;
  filename: string;
  file_size: number;
  file_type: string;
  status: string;
  created_at: string;
}

export async function listKnowledgeFiles(): Promise<KnowledgeFile[]> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Not authenticated');

  const res = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/manage-vector-store?action=list-files`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
      },
    }
  );

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to list files');
  }

  const { files } = await res.json();
  return files;
}

export async function deleteKnowledgeFile(fileId: string): Promise<void> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Not authenticated');

  const res = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/manage-vector-store?action=delete-file`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({ file_id: fileId }),
    }
  );

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to delete file');
  }
}

export async function saveCoachConfig(
  coachId: string,
  config: {
    openaiApiKey: string;
    openaiAssistantId: string;
    elevenlabsApiKey: string;
    elevenlabsAgentId: string;
    isEnabled: boolean;
  }
): Promise<void> {
  const { data: existing } = await supabase
    .from('ai_coach_configs')
    .select('id')
    .eq('coach_id', coachId)
    .maybeSingle();

  const nonSecretPayload = {
    coach_id: coachId,
    openai_assistant_id: config.openaiAssistantId,
    elevenlabs_agent_id: config.elevenlabsAgentId || null,
    is_enabled: config.isEnabled,
    updated_at: new Date().toISOString(),
  };

  if (existing) {
    const { error } = await supabase
      .from('ai_coach_configs')
      .update(nonSecretPayload)
      .eq('id', existing.id);
    if (error) throw error;
  } else {
    const { error } = await supabase
      .from('ai_coach_configs')
      .insert(nonSecretPayload);
    if (error) throw error;
  }

  const trimmedOpenai = config.openaiApiKey.trim();
  const trimmedEleven = config.elevenlabsApiKey.trim();

  if (trimmedOpenai.length > 0 || trimmedEleven.length > 0) {
    const { error: rpcError } = await supabase.rpc('save_ai_coach_secrets', {
      p_coach_id: coachId,
      p_openai_api_key: trimmedOpenai.length > 0 ? trimmedOpenai : null,
      p_elevenlabs_api_key: trimmedEleven.length > 0 ? trimmedEleven : null,
    });
    if (rpcError) throw rpcError;
  }
}

export async function fetchRespondentAccess(coachId: string): Promise<AICoachAccess[]> {
  type AccessRow = { id: string; coach_id: string; respondent_id: string; chat_enabled: boolean; voice_enabled: boolean; max_chat_messages: number; max_voice_minutes: number; max_tokens_per_month: number; created_at: string; updated_at: string };
  const { data } = await (supabase
    .from('ai_coach_access')
    .select('*')
    .eq('coach_id', coachId) as unknown as Promise<{ data: AccessRow[] | null; error: unknown }>);

  return (data || []).map(row => ({
    id: row.id,
    coachId: row.coach_id,
    respondentId: row.respondent_id,
    chatEnabled: row.chat_enabled,
    voiceEnabled: row.voice_enabled,
    maxChatMessages: row.max_chat_messages,
    maxVoiceMinutes: row.max_voice_minutes,
    maxTokensPerMonth: row.max_tokens_per_month ?? 0,
  }));
}

export async function upsertRespondentAccess(
  coachId: string,
  respondentId: string,
  access: { chatEnabled: boolean; voiceEnabled: boolean; maxChatMessages: number; maxVoiceMinutes: number; maxTokensPerMonth: number }
): Promise<void> {
  const { data: existing } = await supabase
    .from('ai_coach_access')
    .select('id')
    .eq('coach_id', coachId)
    .eq('respondent_id', respondentId)
    .maybeSingle();

  const payload = {
    coach_id: coachId,
    respondent_id: respondentId,
    chat_enabled: access.chatEnabled,
    voice_enabled: access.voiceEnabled,
    max_chat_messages: access.maxChatMessages,
    max_voice_minutes: access.maxVoiceMinutes,
    max_tokens_per_month: access.maxTokensPerMonth,
    updated_at: new Date().toISOString(),
  };

  if (existing) {
    const { error } = await supabase
      .from('ai_coach_access')
      .update(payload)
      .eq('id', existing.id);
    if (error) throw error;
  } else {
    const { error } = await supabase
      .from('ai_coach_access')
      .insert(payload);
    if (error) throw error;
  }
}

export async function fetchMyAICoachAccess(respondentId: string): Promise<AICoachAccess | null> {
  const { data } = await (supabase
    .from('ai_coach_access')
    .select('*')
    .eq('respondent_id', respondentId)
    .maybeSingle() as unknown as Promise<{ data: { id: string; coach_id: string; respondent_id: string; chat_enabled: boolean; voice_enabled: boolean; max_chat_messages: number; max_voice_minutes: number; max_tokens_per_month: number; created_at: string; updated_at: string } | null; error: unknown }>);

  if (!data) return null;

  return {
    id: data.id,
    coachId: data.coach_id,
    respondentId: data.respondent_id,
    chatEnabled: data.chat_enabled,
    voiceEnabled: data.voice_enabled,
    maxChatMessages: data.max_chat_messages,
    maxVoiceMinutes: data.max_voice_minutes,
    maxTokensPerMonth: data.max_tokens_per_month ?? 0,
  };
}

export async function fetchCoachConfigForRespondent(coachId: string): Promise<{ isEnabled: boolean; elevenlabsAgentId: string; clientAccessEnabled: boolean } | null> {
  const { data } = await supabase
    .from('ai_coach_configs')
    .select('is_enabled, elevenlabs_agent_id, client_access_enabled')
    .eq('coach_id', coachId)
    .eq('is_enabled', true)
    .maybeSingle();

  if (!data) return null;

  return {
    isEnabled: data.is_enabled,
    elevenlabsAgentId: data.elevenlabs_agent_id || '',
    clientAccessEnabled: data.client_access_enabled || false,
  };
}

export async function logUsage(
  coachId: string,
  respondentId: string,
  type: 'chat' | 'voice',
  extra: { durationSeconds?: number; messageCount?: number; threadId?: string }
): Promise<void> {
  const { error } = await supabase
    .from('ai_coach_usage_logs')
    .insert({
      coach_id: coachId,
      respondent_id: respondentId,
      type,
      duration_seconds: extra.durationSeconds || 0,
      message_count: extra.messageCount || 0,
      thread_id: extra.threadId || null,
    });
  if (error) throw error;
}

export async function fetchUsageSummary(coachId: string): Promise<UsageSummary[]> {
  const { data: accessRows } = await (supabase
    .from('ai_coach_access')
    .select('respondent_id, max_tokens_per_month, max_voice_minutes')
    .eq('coach_id', coachId) as unknown as Promise<{ data: Array<{ respondent_id: string; max_tokens_per_month: number; max_voice_minutes: number }> | null; error: unknown }>);

  if (!accessRows || accessRows.length === 0) return [];

  const respondentIds = accessRows.map(r => r.respondent_id);
  const accessMap = new Map<string, { maxTokensPerMonth: number; maxVoiceMinutes: number }>();
  accessRows.forEach(r => {
    accessMap.set(r.respondent_id, {
      maxTokensPerMonth: r.max_tokens_per_month ?? 0,
      maxVoiceMinutes: r.max_voice_minutes ?? 0,
    });
  });

  // Current month start for token aggregation
  const monthStart = new Date();
  monthStart.setDate(1);
  monthStart.setHours(0, 0, 0, 0);

  const [respondentsRes, logsRes] = await Promise.all([
    supabase
      .from('respondents')
      .select('id, user_id, users!respondents_user_id_fkey(full_name, email)')
      .in('id', respondentIds),
    supabase
      .from('ai_coach_usage_logs')
      .select('respondent_id, type, duration_seconds, message_count, token_input, token_output, created_at')
      .eq('coach_id', coachId)
      .order('created_at', { ascending: false }) as unknown as Promise<{ data: Array<{ respondent_id: string; type: string; duration_seconds: number | null; message_count: number; token_input: number | null; token_output: number | null; created_at: string }> | null; error: unknown }>,
  ]);

  const respondentMap = new Map<string, { name: string; email: string }>();
  (respondentsRes.data || []).forEach(r => {
    respondentMap.set(r.id, {
      name: (r.users as any)?.full_name || (r.users as any)?.email || 'Unknown',
      email: (r.users as any)?.email || '',
    });
  });

  const usageMap = new Map<string, {
    chatMessages: number;
    voiceSeconds: number;
    monthlyTokens: number;
    lastActivity: string | null;
  }>();

  (logsRes.data || []).forEach(log => {
    const existing = usageMap.get(log.respondent_id) || { chatMessages: 0, voiceSeconds: 0, monthlyTokens: 0, lastActivity: null };
    if (log.type === 'chat') {
      existing.chatMessages += log.message_count;
      if (log.created_at >= monthStart.toISOString()) {
        existing.monthlyTokens += (log.token_input || 0) + (log.token_output || 0);
      }
    } else {
      existing.voiceSeconds += log.duration_seconds || 0;
    }
    if (!existing.lastActivity || log.created_at > existing.lastActivity) {
      existing.lastActivity = log.created_at;
    }
    usageMap.set(log.respondent_id, existing);
  });

  return respondentIds.map(rid => {
    const info = respondentMap.get(rid) || { name: 'Unknown', email: '' };
    const usage = usageMap.get(rid) || { chatMessages: 0, voiceSeconds: 0, monthlyTokens: 0, lastActivity: null };
    const limits = accessMap.get(rid) || { maxTokensPerMonth: 0, maxVoiceMinutes: 0 };
    return {
      respondentId: rid,
      respondentName: info.name,
      respondentEmail: info.email,
      totalChatMessages: usage.chatMessages,
      totalVoiceMinutes: Math.round(usage.voiceSeconds / 60),
      totalTokens: usage.monthlyTokens,
      maxTokensPerMonth: limits.maxTokensPerMonth,
      maxVoiceMinutes: limits.maxVoiceMinutes,
      lastActivity: usage.lastActivity,
    };
  });
}
